/**
 * incident.io status page Widget API.
 *
 * Public, unauthenticated endpoint that returns the live status of the Allo
 * status page (ongoing incidents + maintenances). Documented at
 * https://docs.incident.io/status-pages/api#widget-api — the URL is specific
 * to our page and taken from the incident.io dashboard's Widget API settings.
 *
 * Response uses snake_case, matching the rest of our external API contracts.
 */

/** URL of our status page's Widget API. Same across all environments. */
export const STATUS_PAGE_WIDGET_URL = 'https://status.withallo.com/api/v1/summary';

/** Public status page, used as a link fallback when the API omits a URL. */
export const STATUS_PAGE_PUBLIC_URL = 'https://status.withallo.com/';

export type IncidentStatus = 'investigating' | 'identified' | 'monitoring';

export type IncidentImpact = 'degraded_performance' | 'partial_outage' | 'full_outage';

export type ComponentStatus =
  | 'operational'
  | 'degraded_performance'
  | 'partial_outage'
  | 'full_outage';

export type MaintenanceStatus = 'maintenance_in_progress' | 'maintenance_scheduled';

export type AffectedComponent = {
  id: string;
  name: string;
  group_name?: string | null;
  current_status: ComponentStatus;
};

export type OngoingIncident = {
  id: string;
  name: string;
  status: IncidentStatus;
  url: string;
  last_update_at: string;
  last_update_message: string;
  current_worst_impact: IncidentImpact;
  affected_components: AffectedComponent[];
};

export type Maintenance = {
  id: string;
  name: string;
  status: MaintenanceStatus;
  url: string;
  last_update_at: string;
  last_update_message: string;
  affected_components: AffectedComponent[];
  /** Present on in-progress maintenances. */
  started_at?: string;
  scheduled_end_at?: string;
  /** Present on scheduled maintenances. */
  starts_at?: string;
  ends_at?: string;
};

export type StatusPageSummary = {
  page_title: string;
  page_url: string;
  ongoing_incidents: OngoingIncident[];
  in_progress_maintenances: Maintenance[];
  scheduled_maintenances: Maintenance[];
};

/**
 * Fetches the live status page summary. Throws on a non-2xx response so
 * react-query can surface the error (the banner simply stays hidden).
 */
export const fetchStatusPageSummary = async (signal?: AbortSignal): Promise<StatusPageSummary> => {
  const response = await fetch(STATUS_PAGE_WIDGET_URL, {
    signal,
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Status page request failed with ${response.status}`);
  }

  return response.json() as Promise<StatusPageSummary>;
};

/** Visual tone of the banner, derived from the worst active impact. */
export type StatusBannerSeverity = 'outage' | 'degraded' | 'maintenance';

export type StatusBannerState = {
  severity: StatusBannerSeverity;
  /** Heading, taken verbatim from the incident/maintenance name. */
  name: string;
  /** Latest update text from the API. May be empty. */
  message: string;
  /** Link to the specific incident/maintenance, or the status page. */
  url: string;
};

const IMPACT_RANK: Record<IncidentImpact, number> = {
  degraded_performance: 1,
  partial_outage: 2,
  full_outage: 3,
};

const resolveUrl = (itemUrl: string, summary: StatusPageSummary): string =>
  itemUrl || summary.page_url || STATUS_PAGE_PUBLIC_URL;

/**
 * Derives the single most relevant thing to surface in the banner, or `null`
 * when nothing is active. Priority: ongoing incidents (worst impact wins) →
 * in-progress maintenance → scheduled maintenance. All user-facing copy comes
 * from the API; only the visual severity is computed here.
 */
export const getStatusBannerState = (
  summary: StatusPageSummary | undefined,
): StatusBannerState | null => {
  if (!summary) {
    return null;
  }

  const incidents = summary.ongoing_incidents ?? [];
  if (incidents.length > 0) {
    const worst = incidents.reduce((acc, incident) =>
      IMPACT_RANK[incident.current_worst_impact] > IMPACT_RANK[acc.current_worst_impact]
        ? incident
        : acc,
    );

    return {
      severity:
        IMPACT_RANK[worst.current_worst_impact] >= IMPACT_RANK.partial_outage
          ? 'outage'
          : 'degraded',
      name: worst.name,
      message: worst.last_update_message ?? '',
      url: resolveUrl(worst.url, summary),
    };
  }

  const inProgress = summary.in_progress_maintenances ?? [];
  const scheduled = summary.scheduled_maintenances ?? [];
  const maintenance = inProgress[0] ?? scheduled[0];
  if (maintenance) {
    return {
      severity: 'maintenance',
      name: maintenance.name,
      message: maintenance.last_update_message ?? '',
      url: resolveUrl(maintenance.url, summary),
    };
  }

  return null;
};
